<script lang="ts" setup>
import { BaseObjDao } from '@/dao/BaseObjDao';
import { StationRole } from '@/objects/generated/classes/StationRole';
import { useEventCommonStoreRef, useEventStore } from '@/stores';
import { ref, computed, watch } from 'vue';
import { AlertMessage, ContentComp, TabComp, UnsavedChangesMessage } from '@/components';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import type { FieldAlertMessage } from '@/objects';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import { featureIcons } from '@/.';
import StationRoleTrackingView from './StationRoleTrackingView.vue';
import StationRoleStartcontrolView from './StationRoleStartcontrolView.vue';
import StationRoleResultView from './StationRoleResultView.vue';
import StationRoleGPIOView from './StationRoleGPIOView.vue';
import StationRoleGeneralView from './StationRoleGeneralView.vue';
import { useLoaders, useTaskLoader } from '@/composeables';
import StationRoleRelayView from './StationRoleRelayView.vue';
import { getStationRoleErrors, getStationRoleWarnings } from '../Station/stationHelpers';
import { getDateWithMaxTime, getDateWithMinTime } from '@/utilities/dateutils';
import { TimeSpan } from '@/objects/TimeSpan';

const props = defineProps<{
    id: number;

    item?: StationRole;
}>();

const eventStore = useEventStore();

const { Event, StationRoles, Stations, Races } = useEventCommonStoreRef();

const model = ref<StationRole | null>(null);
const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['STATIONROLES']);

const changed = computed(() => (model.value?.EntityState ?? 'Unchanged') !== 'Unchanged');

// Define the tabs
const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('general', 'stationrole.stationrole', featureIcons.STATIONROLES));
tabs.AddTab(new TabDefinition('startcontrol', 'stationrole.startcontrol', 'fa-check'));
tabs.AddTab(new TabDefinition('gpio', 'stationrole.gpio', featureIcons.READINGS));
tabs.AddTab(new TabDefinition('result', 'stationrole.result', 'fa-ranking-star'));
tabs.AddTab(new TabDefinition('relay', 'contestant.relay', 'fa-users'));
tabs.AddTab(new TabDefinition('tracking', 'stationrole.tracking', featureIcons.TRACKING));

AddLoader(async () => {
    if (props.item !== undefined) {
        model.value = props.item;
    } else {
        model.value = props.item ?? (await BaseObjDao.LoadOrCreateSingle(StationRole, props.id, eventStore.eventID));

        if (props.id < 0) {
            model.value.StartTime = getDateWithMinTime(Event.value?.EventStart ?? Event.value?.Date ?? new Date());
            model.value.StopTime = getDateWithMaxTime(Event.value?.EventEnd ?? Event.value?.Date ?? new Date());
            model.value.Public = true;
            model.value.MinLapTime = TimeSpan.FromSeconds(20);
            model.value.StationID = Stations.value.length > 0 ? Stations.value[0].ID : 0;
            model.value.RaceID = Races.value.length > 0 ? Races.value[0].ID : 0;
        }
    }
}, 'stationrole.loading_stationrole');

const computedValForExpose = computed(() => {
    return model.value;
});

defineExpose<DetailViewExpose<StationRole>>({
    val: computedValForExpose,
    title: computed(() => model.value?.Name ?? ''),
    changed: computed(() => changed.value),
    messages: computed(() => (detailComp.value !== null ? detailComp.value!.messages : [])),
});
</script>

<template>
    <ContentComp :load-state="LoadState">
        <UnsavedChangesMessage
            v-if="changed"
            class="mb-2"
        />

        <AlertMessage
            v-if="
                model !== null &&
                Event !== null &&
                (model.EntityState != 'Added' || changed) &&
                getStationRoleErrors(model!, Event!, StationRoles).length > 0
            "
            type="danger"
            :show-icon="false"
            class="flex-fill"
            innerclass="flex-fill"
        >
            <AlertMessage
                type="danger"
                class="mb-1"
                size="xs"
                v-for="(message, i) in getStationRoleErrors(model!, Event!, StationRoles)"
                :key="i"
                :show-icon="false"
                :fulldismiss-button="true"
                :globalmessage="true"
            >
                <span v-html="$tm(message, '', true)"></span>
            </AlertMessage>
        </AlertMessage>

        <AlertMessage
            v-if="
                model !== null &&
                Event !== null &&
                (model.EntityState != 'Added' || changed) &&
                getStationRoleWarnings(model!, Event!, StationRoles).length > 0
            "
            type="warning"
            :show-icon="false"
            class="flex-fill"
            innerclass="flex-fill"
        >
            <AlertMessage
                type="warning"
                class="mb-1"
                size="xs"
                v-for="(message, i) in getStationRoleWarnings(model!, Event!, StationRoles)"
                :key="i"
                :show-icon="false"
                :fulldismiss-button="true"
                :globalmessage="true"
            >
                <span v-html="$tm(message, '', true)"></span>
            </AlertMessage>
        </AlertMessage>

        <TabComp
            :tabs="tabs"
            :pinable="false"
            :hide-tabs-on-only-one="true"
            v-if="model != null"
        >
            <template #tab-general>
                <StationRoleGeneralView
                    v-if="model != null"
                    v-model="model"
                />
            </template>
            <template #tab-startcontrol
                ><StationRoleStartcontrolView
                    v-if="model != null"
                    v-model="model"
            /></template>
            <template #tab-result>
                <StationRoleResultView
                    v-if="model != null"
                    v-model="model"
                />
            </template>
            <template #tab-gpio
                ><StationRoleGPIOView
                    v-if="model != null"
                    v-model="model"
            /></template>
            <template #tab-tracking>
                <StationRoleTrackingView
                    v-if="model != null"
                    v-model="model"
                />
            </template>
            <template #tab-relay>
                <StationRoleRelayView
                    v-if="model != null"
                    v-model="model"
                />
            </template>
        </TabComp>
    </ContentComp>
</template>
